import { compose, mdsWidget } from 'dojo/_base/declare';
import _EntityViewMixin from '../../moreCore/UI/EntityBased/EntityView/EntityView';
import TempInvoiceEditor from './TempInvoiceEditor';
import StaticTable from '../../moreCore/UI/StaticTable/StaticTable';
import lang from 'dojo/_base/lang';
import { ExistingMusicianPlanningResponse, MusicianPlanning, TempInvoiceFinderProject, TempInvoicePresetData } from "./TempInvoiceTypes";
import GenericDialog from '../../moreCore/UI/GenericDialog/GenericDialog';
import { GenericDialogTypes } from "../../moreCore/UI/GenericDialog/GenericDialogTypes";
import TempInvoiceConfirmationEditor from './TempInvoiceConfirmationEditor';
import _WidgetBase from 'dijit/_WidgetBase';

class TempInvoiceView extends compose(_EntityViewMixin<ExistingMusicianPlanningResponse>) {

    private project;
    private parent;

    /**
     * 
     * @param entityId
     * @param label
     * @param additionalParams
     * @param tabContainerName
     * @param contextName Beliebiger Kontextname, der im Backend eine Differenzierung zum Zweck der Ansicht zulässt
     */
    setup(entityId: string,
        label: string,
        entity: ExistingMusicianPlanningResponse,
        additionalParams?: Object,
        contextName?: string,
        tabContainerName?: string,

    ) {
        this.tabContainerName = tabContainerName;
        this.entity = entity;
        this.parent = additionalParams["parent"];


        if (contextName) this.setContextName(contextName);

        this.showAsDlg(entityId, label, tabContainerName);
    }

    _afterLoaded() {

        if (this.contextName === "tempInvoiceProjectView") {
            this.editingAllowed = this._isEditAllowed();
            this.deletingAllowed = this._isEditAllowed();

            this.createDefaultButton("delete", lang.hitch(this._onClickDelete));

            this.addButtonMenuEx({
                internalName: "planningStatus",
                iconClass: "fas fa-user",
                bIsGroupNextLevel: true,
                label: "TempInvoice_Label_Status"
            });

            if (this._isEditAllowed()) {
                this.addButtonEx({
                    callback: lang.hitch(this, this._onClickCancel),
                    internalName: "cancel",
                    label: "TempInvoice_Label_Cancel",
                    iconClass: "fas fa-times primaryTXT",
                    position: "secondary",
                    groupname: "planningStatus",
                    bCloseOnClick: false,
                    enabled: this._isEditAllowed()
                });

                this.addButtonEx({
                    callback: lang.hitch(this, this._onClickConfirm),
                    internalName: "confirm",
                    label: "TempInvoice_Label_Confirm",
                    iconClass: "fas fa-check successTXT",
                    position: "secondary",
                    groupname: "planningStatus",
                    bCloseOnClick: false,
                    enabled: this._isConfirmAllowed(),
                });
            }
        }
        else if (this.contextName == "tempInvoiceConfirmationView") {
            this.editingAllowed = this._isEditAllowedConfirmation();
            this.editingEnabled = this._isEditAllowedConfirmation();
        }
        else {
            this.editingAllowed = false;
            this.deletingAllowed = false;
        }
    
    }

    private _isEditAllowedConfirmation() {
        return this.entity.is_substitute_confirmed && !this.entity.is_input_confirmed && !this.entity.is_substitute_canceled;
    }

    private _isEditAllowed() {
        let dateto = new Date(this.entity.ref_ev_project.date_to);
        let musicianplannings = this.entity;
        let maxDaysAfterEnd = null;
        this.entity.maxDaysAfterEnd;
        let isSubstituteConfirmed =  this.entity ? this.entity?.is_substitute_confirmed : false;

        if (dateto && !isSubstituteConfirmed && !this.entity.is_substitute_canceled) {
            dateto.setDate(dateto.getDate() + maxDaysAfterEnd);
            dateto.setHours(0, 0, 0, 0);

            let today = new Date(Date.now());
            today.setHours(0, 0, 0, 0);


            let editallowed = today <= dateto;
            return editallowed;
        } else {
            return false;
        }
    }

    private _isConfirmAllowed(): boolean {
        return this.entity ? !this.entity?.is_substitute_confirmed : false;
    }

    private _onClickConfirm() {
        let dlg = new GenericDialog();
        dlg.setup({
            buttonPreset: "yesno",
            content: "TempInvoiceEditor_Message_ConfirmTemp",
            contentIconClass: GenericDialogTypes.DialogIcon.warning,
            header: "TempInvoiceEditor_Header_ConfirmTemp",
            callbackConfig: {
                yesCallback: lang.hitch(this, this._sendConfirmRequest)
            }
        });
    }

    private _sendConfirmRequest() {
        this.StandbyStart();
        this.RequestManager.callfunction("Webtool_API__TempInvoiceEditor_ConfirmMusicianPlanning", { entityID: this.entity.ID }, lang.hitch(this, this._onConfirmResponse), lang.hitch(this, this._onConfirmError));
    }

    private _onConfirmResponse(response: ExistingMusicianPlanningResponse) {
        this.StandbyStop();
        this.showToast({ title: "TempInvoice_Success_ConfirmMusicianPlanning", iconClass: "fas fa-check", timeout: 3000 });
        this.emit("saved");
        this.reload();
    }

    private _onConfirmError(error: more.requestErrorMessage) {
        this.StandbyStop();
        this.showToast({ title: "TempInvoice_Error_ConfirmMusicianPlanning", iconClass: "fas fa-exclamation-triangle", timeout: 3000 });
    }

    private _onClickCancel() {
        let dlg = new GenericDialog();
        dlg.setup({
            buttonPreset: "yesno",
            content: "TempInvoiceEditor_Message_CancelTemp",
            contentIconClass: GenericDialogTypes.DialogIcon.warning,
            header: "TempInvoiceEditor_Header_CancelTemp",
            callbackConfig: {
                yesCallback: lang.hitch(this, this._sendCancelRequest)
            }
        });
    }

    private _sendCancelRequest() {
        this.StandbyStart();
        this.RequestManager.callfunction("Webtool_API__TempInvoiceEditor_CancelMusicianPlanning", { entityID: this.entity.ID }, lang.hitch(this, this._onCancelResponse), lang.hitch(this, this._onCancelError));
    }

    private _onCancelResponse(response: ExistingMusicianPlanningResponse) {
        this.StandbyStop();
        this.showToast({ title: "TempInvoice_Success_CancelMusicianPlanning", iconClass: "fas fa-check", timeout: 3000 });
        this.reload();
        this.emit("saved");
    }

    private _onCancelError(error: more.requestErrorMessage) {
        this.StandbyStop();
        this.showToast({ title: "TempInvoice_Error_CancelMusicianPlanning", iconClass: "fas fa-exclamation-triangle", timeout: 3000 });
    }


    protected onRequestEditor(): MdsLax<InstanceType<typeof _WidgetBase>> {
        if (this.contextName == "tempInvoiceProjectView") {
            let editor = new TempInvoiceEditor();
            editor.setup(this.entity.ref_ev_project, null, this.entity);
            editor.on("saved", lang.hitch(this, this._onEditorSaved));
            this.emit("editorOpened", editor);
            this.close();

            return editor;
        } else if (this.contextName == "tempInvoiceConfirmationView") {
            let editor = new TempInvoiceConfirmationEditor();
            editor.setup(this.entity, this.tabContainerName);
            editor.on("saved", lang.hitch(this, this._onEditorSaved));
            this.emit("editorOpened", editor);
            this.close();

            return editor;
        }

    }

    private _onEditorSaved() {
        this.emit("saved");
    }

    protected _onClickDelete(rowGroup: StaticTable.StaticTableRowGroup<ExistingMusicianPlanningResponse>) {
        let dlg = new GenericDialog();
        dlg.setup({
            buttonPreset: "yesno",
            content: "TempInvoiceEditor_Message_DeleteTemp",
            contentIconClass: GenericDialogTypes.DialogIcon.warning,
            header: "TempInvoiceEditor_Header_DeleteTemp",
            callbackConfig: {
                yesCallback: lang.hitch(this, this._sendDeleteRequest, rowGroup.entity)
            }
        });
    }

    private _sendDeleteRequest() {
        this.StandbyStart();
        this.RequestManager.callfunction(
            "Webtool_API__TempInvoiceEditor_DeleteData",
            { ID: this.entity.ID },
            lang.hitch(this, this._onDeleteResponse),
            lang.hitch(this, this._onDeleteErr)
        );
    }

    private _onDeleteResponse(response) {
        this.StandbyStop();
        this.close();
        this.emit("saved");
    }

    private _onDeleteErr(error) {
        console.error(error);
    }

    postCreate(): void {

        this.config = {
            entityClassname: MusicianPlanning,
            labelProperty: "$Displayname",
            entityClassPrintname: this.translate("General_Label_MusicianPlanning"),
            editOptions: {
                userroleEdit: ["TEMPINVOICEASSIGNMENT", "TEMPINVOICECONFIRMATION"],
                onChangedEventName: "saved",
                onDeletedEventName: "saved",
                onSavedEventName: "saved"
            },
            deleteOptions: {
                userroleDelete: "TEMPINVOICEASSIGNMENT"
            }

            
            
        }
        super.postCreate.apply(this, arguments as any);
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(TempInvoiceView);